import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Grid, Tabs, Tab, Paper, Divider, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
  FormControlLabel, FormGroup
} from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import { Link, useNavigate } from 'react-router-dom';
import { apigetPublic } from '../../service/api';
import LandingPageNav from '../landingpage/LandingPageNav';
import InternalLOAutocomplete from '../localSearch/localSearch';
import Footer from '../Footer/footer';

export default function FindALoanOfficer() {

  // LIVE FILTERED RESULTS
  const [searchLocation, setSearchLocation] = useState('');
  const [loanOfficers, setLoanOfficers] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);

  // ORIGINAL UNFILTERED DATA
  const [allLoanOfficers, setAllLoanOfficers] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [allBranches, setAllBranches] = useState([]);

  // Modal logic
  const [selectedState, setSelectedState] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [openStateModal, setOpenStateModal] = useState(false);

  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();

  // ------------------------------------------------------------
  // Load Loan Officers + States + Branches
  // ------------------------------------------------------------
  const loadData = async () => {
    const loRes = await apigetPublic('loanofficer/search');
    if (loRes?.status === 200) {
      setAllLoanOfficers(loRes.data.result || []);
      setLoanOfficers(loRes.data.result || []);
    }

    const stateRes = await apigetPublic('states/search');
    if (stateRes?.status === 200) {
      setAllStates(stateRes.data.result || []);
      setStates(stateRes.data.result || []);
    }

    const branchRes = await apigetPublic('branches/search');
    if (branchRes?.status === 200) {
      setAllBranches(branchRes.data.result || []);
      setBranches(branchRes.data.result || []);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ------------------------------------------------------------
  // Search Loan Officers (manual search button)
  // ------------------------------------------------------------
  const handleSearch = async (inputLocation) => {
    const location = (inputLocation ?? searchLocation)?.trim();
    const result = await apigetPublic(
      location
        ? `loanofficer/search?location=${encodeURIComponent(location)}`
        : `loanofficer/search`
    );

    if (result?.status === 200) {
      setLoanOfficers(result.data.result || []);
    }
  };

  // ------------------------------------------------------------
  // Handle State Click
  // ------------------------------------------------------------
const handleStateClick = (stateObj) => {
  setSelectedState(stateObj);

  const opts = stateObj.additionalOptions || [];

  // ---- If no additional options ----
  if (opts.length === 0) {
    // No options → auto-show all loan officers in that state
    setSelectedOptions([]); // Show ALL officers for that state
    setOpenStateModal(true);
    return;
  }

  // ---- If there are additional options ----
  setSelectedOptions([opts[0]]);
  setOpenStateModal(true);
};



  // ------------------------------------------------------------
  // Filter Loan Officers (Modal)
  // ------------------------------------------------------------
  const getFilteredOfficers = () => {
    if (!selectedState) return [];

    return loanOfficers.filter((lo) => {
      if (!lo.states?.includes(selectedState.name)) return false;

      if (selectedOptions.length === 0) return true;

      const opt = selectedOptions[0];
      return lo.stateOptionsSelected?.includes(`${selectedState.name}::${opt}`);
    });
  };

  const filteredOfficers = getFilteredOfficers();

  const toggleOption = (opt) => {
    setSelectedOptions([opt]);
  };

  // ------------------------------------------------------------
  // LIVE TYPING FILTER
  // ------------------------------------------------------------
  useEffect(() => {
    if (!searchLocation) {
      setLoanOfficers(allLoanOfficers);
      setStates(allStates);
      setBranches(allBranches);
      return;
    }

    const term = searchLocation.toLowerCase();

    setLoanOfficers(
      allLoanOfficers.filter((lo) =>
        `${lo.firstName} ${lo.lastName}`.toLowerCase().includes(term) ||
        lo.city?.toLowerCase().includes(term) ||
        lo.state?.toLowerCase().includes(term)
      )
    );

    setStates(
      allStates.filter((s) =>
        s.name.toLowerCase().includes(term) ||
        s.code.toLowerCase().includes(term)
      )
    );

    setBranches(
      allBranches.filter((b) =>
        b.name.toLowerCase().includes(term) ||
        b.city.toLowerCase().includes(term)
      )
    );
  }, [searchLocation, allLoanOfficers, allStates, allBranches]);

  // ------------------------------------------------------------
  // AUTO SWITCH TABS BASED ON FILTERED RESULTS
  // ------------------------------------------------------------
  useEffect(() => {
    // If current tab still has results, do nothing
    if (tabValue === 0 && loanOfficers.length > 0) return;
    if (tabValue === 1 && states.length > 0) return;
    if (tabValue === 2 && branches.length > 0) return;

    // Otherwise pick the first non-empty tab
    if (loanOfficers.length > 0) {
      setTabValue(0);
    } else if (states.length > 0) {
      setTabValue(1);
    } else if (branches.length > 0) {
      setTabValue(2);
    }
  }, [loanOfficers, states, branches, tabValue]);

  // ------------------------------------------------------------
  // Render UI
  // ------------------------------------------------------------
  return (
    <>
      <LandingPageNav />

      <Box sx={{ px: 4, pt: 12, pb: 6, maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
          Find a Loan Officer, State, or Branch
        </Typography>

        <Typography align="center" mb={4}>
          Search loan officers or explore our locations.
        </Typography>

        {/* SEARCH INPUT */}
        <Grid container spacing={2} justifyContent="center" alignItems="center" mb={3}>
          <Grid item xs={12} md={8}>
            <InternalLOAutocomplete
              loanOfficers={loanOfficers}
              branches={branches}
              value={searchLocation}
              onChange={setSearchLocation}
              onSubmit={(payload) => {
                if (payload?.type === "state") {
                  const state = states.find(
                    (s) => s.code.toLowerCase() === payload.stateCode.toLowerCase()
                  );
                  if (state) handleStateClick(state);
                  return;
                }

                handleSearch(payload);
              }}
            />
          </Grid>

          <Grid item xs={12} md="auto">
            <Button variant="contained" onClick={() => handleSearch()}>
              Search
            </Button>
          </Grid>
        </Grid>

        {/* TABS */}
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} centered>
          <Tab label={`Loan Officers (${loanOfficers.length})`} />
          <Tab label={`States (${states.length})`} />
          <Tab label={`Branches (${branches.length})`} />
        </Tabs>

        <Divider sx={{ my: 2 }} />

        {/* TAB 1 — Loan Officers */}
        {tabValue === 0 &&
          loanOfficers.map((o, idx) => (
            <ButtonBase
              key={`${o.slug}-${idx}`}
              component={Link}
              to={`/${o.slug}`}
              sx={{ width: '100%', textAlign: 'left', mb: 2 }}
            >
              <Paper sx={{ p: 3, width: '100%', display: 'flex', alignItems: 'center' }}>
                <Avatar src={o.imageUrl} sx={{ width: 100, height: 100, mr: 3 }} />
                <Box>
                  <Typography variant="h6">{o.firstName} {o.lastName}</Typography>
                  <Typography>{o.title}</Typography>
                  <Typography>{o.phone}</Typography>
                  <Typography>{o.email}</Typography>
                </Box>
              </Paper>
            </ButtonBase>
          ))}

        {/* TAB 2 — STATES */}
        {tabValue === 1 &&
          states.map((state) => {
            const img = state.branchImageUrl || "/assets/images/branch-default.jpg";

            return (
              <ButtonBase
                key={state._id}
                onClick={() => handleStateClick(state)}
                sx={{ width: '100%', textAlign: 'left', mb: 2 }}
              >
                <Paper
                  sx={{
                    p: 3,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    borderRadius: 3,
                    transition: '.2s',
                    '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
                  }}
                >
                  <img
                    src={img}
                    alt={state.name}
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: 8,
                      objectFit: 'cover',
                      marginRight: 20
                    }}
                  />
                  <Box>
                    <Typography variant="h6">
                      {state.name} ({state.code})
                    </Typography>
                  </Box>
                </Paper>
              </ButtonBase>
            );
          })}

        {/* TAB 3 — BRANCHES */}
        {tabValue === 2 &&
          branches.map((branch) => (
            <ButtonBase
              key={branch._id}
              component={Link}
              to={`/branch/${branch.slug}`}
              sx={{ width: '100%', textAlign: 'left', mb: 2 }}
            >
              <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar
                  src={branch.branchImageUrl}
                  sx={{ width: 100, height: 100, mr: 3 }}
                />
                <Box>
                  <Typography variant="h6">{branch.name}</Typography>
                  <Typography>{branch.address}</Typography>
                  <Typography>{branch.city}, {branch.stateCode} {branch.zip}</Typography>
                </Box>
              </Paper>
            </ButtonBase>
          ))}

        {/* STATE MODAL */}
        <Dialog open={openStateModal} onClose={() => setOpenStateModal(false)} maxWidth="lg" fullWidth>
          {selectedState && (
            <>
              <DialogTitle>
                {selectedState.name} 
              </DialogTitle>

              <DialogContent>
                <Grid container spacing={3}>

                 {/* LEFT SIDE — Options (only show if options exist) */}
  {selectedState.additionalOptions?.length > 0 && (
    <Grid item xs={12} md={4}>
      <Typography variant="h6">Options</Typography>

      <FormGroup>
        {selectedState.additionalOptions.map((opt) => (
          <FormControlLabel
            key={opt}
            control={
              <Checkbox
                checked={selectedOptions.includes(opt)}
                onChange={() => toggleOption(opt)}
              />
            }
            label={opt}
          />
        ))}
      </FormGroup>
    </Grid>
  )}

                 {/* RIGHT SIDE — Matching Officers */}
<Grid
  item
  xs={12}
  md={selectedState.additionalOptions?.length > 0 ? 8 : 12}
>
  <Typography variant="h6" sx={{ mb: 2 }}>
    {filteredOfficers.length} Matching Loan Officers
  </Typography>

  {filteredOfficers.map((officer) => (
    <Paper
      key={officer.slug}
      sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center' }}
    >
      <Avatar src={officer.imageUrl} sx={{ width: 70, height: 70, mr: 2 }} />
      <Box>
        <Typography variant="h6">
          {officer.firstName} {officer.lastName}
        </Typography>
        <Typography>{officer.title}</Typography>
        <Button
          component={Link}
          to={`/${officer.slug}`}
          variant="text"
          sx={{ mt: 1 }}
        >
          View Profile
        </Button>
      </Box>
    </Paper>
  ))}

  {filteredOfficers.length === 0 && (
    <Typography>No officers match this option.</Typography>
  )}
</Grid>


                </Grid>
              </DialogContent>

              <DialogActions>
                <Button onClick={() => setOpenStateModal(false)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>

      <Footer />
    </>
  );
}
